import React from "react";
import { useQuery, gql } from "@apollo/client";
import { useI18n } from "../i18n";
import { Location } from "../hooks/useCurrentLocation";
import * as utils from "../utils/utils";

type StationInfoProps = {
    stationId: string;
    location?: Location;
    isFavourite: boolean;
    onToggleFavourite: (stationId: string) => void;
};

type VehicleRentalStationQuery = {
    vehicleRentalStation: VehicleRentalStationItem | null;
};

type VehicleRentalStationItem = {
    stationId: string;
    name: string;
    availableVehicles: { total: number } | null;
    availableSpaces: { total: number } | null;
    allowDropoffNow: boolean | null;
    operative: boolean | null;
    lat: number;
    lon: number;
};

export const STATION_INFO = gql`
    query StationInfo($id: String!) {
        vehicleRentalStation(id: $id) {
            stationId
            name
            availableVehicles {
                total
            }
            availableSpaces {
                total
            }
            allowDropoffNow
            operative
            lat
            lon
        }
    }
`;

const Icon = ({ name, className = "" }: { name: string; className?: string }) => (
    <span className={`material-icons ${className}`} aria-hidden="true">
        {name}
    </span>
);

const StationSkeleton = () => (
    <article className="station station--loading" aria-busy="true">
        <div className="station__main">
            <div className="skeleton skeleton--title" />
            <div className="skeleton skeleton--text" />
        </div>
        <div className="skeleton skeleton--count" />
    </article>
);

const StationInfo: React.FC<StationInfoProps> = ({
    stationId,
    location,
    isFavourite,
    onToggleFavourite,
}) => {
    const { t, locale } = useI18n();
    const { loading, error, data, refetch } =
        useQuery<VehicleRentalStationQuery>(STATION_INFO, {
            variables: { id: utils.normalizeStationId(stationId) },
            notifyOnNetworkStatusChange: false,
        });

    const station = data?.vehicleRentalStation;

    if (loading && !data) return <StationSkeleton />;

    const favouriteButton = (name: string) => (
        <button
            type="button"
            className="station__favourite icon-button"
            aria-pressed={isFavourite}
            aria-label={t(isFavourite ? "removeFavourite" : "addFavourite", {
                name,
            })}
            title={t(isFavourite ? "removeFavourite" : "addFavourite", {
                name,
            })}
            onClick={() => onToggleFavourite(stationId)}
        >
            <Icon name={isFavourite ? "star" : "star_border"} />
        </button>
    );

    if (error && !data) {
        return (
            <article className="station station--message">
                <p className="station__message" role="alert">
                    {t("loadError")}
                </p>
                <button
                    type="button"
                    className="text-button"
                    onClick={() => refetch().catch(() => {})}
                >
                    <Icon name="refresh" /> {t("retry")}
                </button>
            </article>
        );
    }

    if (!station) {
        return (
            <article className="station station--message">
                <p className="station__message">
                    {t("notFound", { id: utils.toShortStationId(stationId) })}
                </p>
                {favouriteButton(utils.toShortStationId(stationId))}
            </article>
        );
    }

    const { name, lat, lon } = station;
    const bikesAvailable = station.availableVehicles?.total ?? 0;
    const operative = station.operative !== false;
    const availability = utils.getAvailability(bikesAvailable, operative);
    const distance =
        location &&
        utils.getDistanceFromLatLonInKm(
            location.latitude,
            location.longitude,
            lat,
            lon
        );

    const details = [
        distance !== undefined &&
            t("distance", {
                distance: utils.formatDistance(distance, locale),
            }),
        operative &&
            station.availableSpaces &&
            t("spaces", { count: station.availableSpaces.total }),
    ].filter(Boolean);

    return (
        <article className={`station station--${availability}`}>
            <div className="station__main">
                <h3 className="station__name">
                    <a
                        href={utils.getWalkingDirectionsUrl(lat, lon)}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={t("directions", { name })}
                    >
                        {name}
                        <Icon name="directions_walk" className="station__walk" />
                    </a>
                </h3>
                {details.length > 0 && (
                    <p className="station__details">
                        {details.map((detail, index) => (
                            <React.Fragment key={index}>
                                {index > 0 && " · "}
                                <span>{detail}</span>
                            </React.Fragment>
                        ))}
                    </p>
                )}
                {!operative && (
                    <p className="station__warning">{t("closed")}</p>
                )}
                {operative && station.allowDropoffNow === false && (
                    <p className="station__warning">
                        {t("returnNotPossible")}
                    </p>
                )}
            </div>
            <div
                className="station__count"
                role="img"
                aria-label={t("bikesLabel", { count: bikesAvailable })}
            >
                <span className="station__number" aria-hidden="true">
                    {bikesAvailable}
                </span>
                <span className="station__unit" aria-hidden="true">
                    {t(bikesAvailable === 1 ? "bikesOne" : "bikesMany")}
                </span>
            </div>
            {favouriteButton(name)}
        </article>
    );
};

export default StationInfo;
